/** Keep in sync with backend `Mivo.Modules.Shared.Clinical.CouncilRequirements`. */
export const COUNCIL_EXEMPT_SPECIALTIES = ["Musicoterapia", "Psicopedagogia"] as const;

const COUNCIL_EXEMPT_SET = new Set<string>(COUNCIL_EXEMPT_SPECIALTIES);

type CouncilFormat = "crp" | "crfa" | "crefitoPhysio" | "crefitoOccupational";

const FORMAT_BY_SPECIALTY: Record<string, CouncilFormat> = {
    Psicologia: "crp",
    Neuropsicologia: "crp",
    Fonoaudiologia: "crfa",
    Fisioterapia: "crefitoPhysio",
    "Terapia Ocupacional": "crefitoOccupational",
};

/**
 * CRP
 * Valid examples:
 * 6/12345
 * CRP 6/12345
 */
const CRP_REGEX = /^(?:CRP\s*)?(?:0?[1-9]|1[0-9]|2[0-9])\/\d{5,}$/i;

/**
 * CRFA
 * Valid examples:
 * 2-12345
 */
const CRFA_REGEX = /^(?:0?[1-9]|1[0-9])-\d{5,}$/i;

/**
 * CREFITO Physiotherapy
 * Valid examples:
 * 6/12345-F
 */
const CREFITO_PHYSIO_REGEX = /^(?:0?[1-9]|1[0-8])\/\d{5,}-F$/i;

/**
 * CREFITO Occupational Therapy
 * Valid examples:
 * 3/12345-TO
 */
const CREFITO_OCCUPATIONAL_REGEX = /^(?:0?[1-9]|1[0-8])\/\d{5,}-TO$/i;

const FORMAT_HINT: Record<CouncilFormat, string> = {
    crp: "Formato inválido. Use 6/12345.",
    crfa: "Formato inválido. Use 2-12345.",
    crefitoPhysio: "Formato inválido. Use 6/12345-F (regiões 1 a 18).",
    crefitoOccupational: "Formato inválido. Use 3/12345-TO.",
};

const FORMAT_PLACEHOLDER: Record<CouncilFormat, string> = {
    crefitoPhysio: "Ex: 6/12345-F",
    crefitoOccupational: "Ex: 3/12345-TO",
    crfa: "Ex: 2-12345",
    crp: "Ex: 6/12345",
};

export function isCouncilExempt(specialty: string | null | undefined): boolean {
    return Boolean(specialty?.trim() && COUNCIL_EXEMPT_SET.has(specialty));
}

export function requiresCouncil(specialty: string | null | undefined): boolean {
    return Boolean(specialty?.trim() && !isCouncilExempt(specialty));
}

function formatForSpecialty(specialty: string): CouncilFormat | null {
    return FORMAT_BY_SPECIALTY[specialty] ?? null;
}

function matchesFormat(format: CouncilFormat, value: string): boolean {
    switch (format) {
        case "crp":
            return CRP_REGEX.test(value);
        case "crfa":
            return CRFA_REGEX.test(value);
        case "crefitoPhysio":
            return CREFITO_PHYSIO_REGEX.test(value);
        case "crefitoOccupational":
            return CREFITO_OCCUPATIONAL_REGEX.test(value);
        default:
            return false;
    }
}

export function validateCouncilNumber(
    specialty: string | null | undefined,
    councilNumber: string | null | undefined,
): { ok: true } | { ok: false; message: string } {
    if (!specialty?.trim()) {
        return { ok: false, message: "Escolha uma especialidade." };
    }

    if (isCouncilExempt(specialty)) {
        return { ok: true };
    }

    const trimmed = councilNumber?.trim() ?? "";
    if (!trimmed) {
        return { ok: false, message: "Número do conselho é obrigatório para esta especialidade." };
    }

    const format = formatForSpecialty(specialty);
    if (!format) {
        return { ok: false, message: "Especialidade inválida para validação do conselho." };
    }

    if (!matchesFormat(format, trimmed)) {
        return { ok: false, message: FORMAT_HINT[format] };
    }

    return { ok: true };
}

export function councilPlaceholder(specialty: string | null | undefined): string {
    const format = specialty ? formatForSpecialty(specialty) : null;
    return format ? FORMAT_PLACEHOLDER[format] : "Informe o número do conselho";
}

export function councilFormatHint(specialty: string | null | undefined): string {
    const format = specialty ? formatForSpecialty(specialty) : null;
    return format ? FORMAT_HINT[format] : "";
}
